// Sprint 18 A4: Presenter Control Room — multi-panel layout
// Chat, Q&A, polls, attendees, CTAs, AI Co-Pilot, engagement meter
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  MessageSquare, HelpCircle, BarChart3, Users, ShoppingBag,
  Brain, Clock, Pin, ThumbsUp,
  Check, Send, Play, Volume2, VolumeX,
  AlertTriangle, Gauge, Radio, LayoutTemplate, MonitorPlay, Presentation, Ban, EyeOff, ShieldAlert, PenLine,
} from "lucide-react";
import {
  useControlRoomStats,
  useControlRoomLayout,
  useProgramLayoutRuntime,
  useEngagement,
  useCopilotSuggestions,
  usePollManager,
  useChatModeration,
  useChatMessages,
  useLayoutPresets,
  useMuteParticipant,
  useBanParticipant,
  useQaQuestions,
  useShadowParticipant,
  useSendChatMessage,
  useSwitchLayout,
  useUpdateChatModerationSettings,
  useAttendees,
  useHandRaiseQueue,
} from "@/hooks/use-webinar-control-room";
import type {
  ChatMessage,
  ChatModerationState,
  QaQuestion,
  AttendeeInfo,
  RaisedHandEntry,
  SpotlightState,
  CopilotSuggestion,
  EngagementData,
  LayoutPreset,
  ControlRoomLayoutSwitchMutationResult,
  ControlRoomLayoutSwitchResult,
  WebinarPollRecord,
  WebinarPollResults,
  CreateWebinarPollInput,
} from "@/hooks/use-webinar-control-room";
import { CtaSequenceManager } from "@/components/pages/webinars/cta-sequence-manager";
import { SlidesPanel } from "@/components/pages/webinars/slides-panel";
import { CanvasRenderer } from "@/components/webinar/canvas-renderer";
import { ModerationDashboard } from "@/components/webinar/ModerationDashboard";
import { formatNumber, formatTime } from "@/lib/i18n";
import { CaptionPresenterControls } from "./captions";

type ActivePanel = "chat" | "moderation" | "qa" | "polls" | "attendees" | "slides" | "canvas" | "captions" | "cta" | "copilot";
type ComposerStatus = "pending" | "sent" | "failed";
type RunnableLayoutPresetPaletteItem = {
  id: LayoutPreset;
  label: string;
  description: string;
  shortcut: string;
  liveOutputSupported: true;
};
type UnsupportedVisionLayoutPresetPaletteItem = {
  id: string;
  label: string;
  description: string;
  shortcut: null;
  liveOutputSupported: false;
  disabledReason: string;
};
type LayoutPresetPaletteItem = RunnableLayoutPresetPaletteItem | UnsupportedVisionLayoutPresetPaletteItem;

const ACTIVE_PACKET_ID = "60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1";
const GENERATED_WORK_ITEM = "60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:WEB-100-006-presenter-production-tools";
const LAYOUT_CONFIRMATION_POLL_INTERVAL_MS = 1_000;
const LAYOUT_CONFIRMATION_TIMEOUT_MS = 30_000;
const BREAKOUT_TIMERS_ROUTE_PATH = "/webinars/:webinarId/breakouts";
const SLIDE_UNSUPPORTED_CONTROLS_REASON = "Slide deck upload, save, current-slide switching, and deck navigation stay available through the mounted slides workflow. Lower thirds, agenda/countdown, and resource links stay disabled here until dedicated live-output seams are mounted. Breakout timers are managed through the mounted webinar breakout route.";
const SLIDE_ROUTE_PROOF = {
  upload: "POST /slides/upload stores slide assets through mediaUploads plus R2 storage and returns a slide DTO, but it does not persist webinars.streamConfig.controlRoom.slides or broadcast webinar.slide.changed by itself.",
  deckPersistence: "PUT /slides persists webinars.streamConfig.controlRoom.slides and broadcasts webinar.slide.changed for live slide output.",
  currentSlide: "POST /slides/current updates webinars.streamConfig.controlRoom.slides.currentIndex and broadcasts webinar.slide.changed for live slide output.",
} as const;

function isLayoutSwitchMutationResult(value: unknown): value is ControlRoomLayoutSwitchMutationResult {
  return value !== null && typeof value === "object" && "ok" in value;
}

type ControlSurfaceState = "live" | "disabled";

interface ControlSurfaceCard {
  key: string;
  label: string;
  state: ControlSurfaceState;
  summary: string;
  detail: string;
  actionLabel: string;
  actionHref?: string;
}

function parseTimestampMs(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function extractRuntimeRevision(programLayout: unknown): number | null {
  if (typeof programLayout !== "object" || programLayout === null) {
    return null;
  }

  // Freshness fix for packet 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:
  // /control-room/program-layout exposes currentLayoutRevision/currentLayoutUpdatedAt on the frontend seam.
  const candidate = (programLayout as { currentLayoutRevision?: unknown; revision?: unknown }).currentLayoutRevision
    ?? (programLayout as { revision?: unknown }).revision;
  return typeof candidate === "number" ? candidate : null;
}

function extractRuntimeUpdatedAt(programLayout: unknown): string | null {
  if (typeof programLayout !== "object" || programLayout === null) {
    return null;
  }

  const candidate = (programLayout as { currentLayoutUpdatedAt?: unknown; updatedAt?: unknown }).currentLayoutUpdatedAt
    ?? (programLayout as { updatedAt?: unknown }).updatedAt;
  return typeof candidate === "string" ? candidate : null;
}

function getLayoutSwitchFailureMessage(error: unknown, fallbackMessage: string): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
}

const CONTROL_ROOM_CURRENT_WEBINAR_VISION_GAP_MAP = [
  {
    surface: "layout presets in live output",
    state: "live",
    detail: "Layout preset switches are confirmed against live program-output telemetry instead of stored preset state alone.",
    proof: "/control-room/program-layout exposes currentLayout plus runtimeAvailable, currentLayoutRevision, and currentLayoutUpdatedAt, and requested presets stay pending until refreshed runtime telemetry confirms the live output layout. When runtime freshness metadata is available, confirmation waits for a newer currentLayoutRevision or currentLayoutUpdatedAt; otherwise the UI waits for a post-request runtime poll cycle before marking the layout live.",
  },
  {
    surface: "slide deck import and deck creation",
    state: "live",
    detail: "The mounted slides workflow keeps upload, deck persistence, and live current-slide switching available from the control room.",
    proof: `${SLIDE_ROUTE_PROOF.upload} ${SLIDE_ROUTE_PROOF.deckPersistence} ${SLIDE_ROUTE_PROOF.currentSlide} ${SLIDE_UNSUPPORTED_CONTROLS_REASON}`,
  },
  {
    surface: "spotlight handoff",
    state: "live",
    detail: "Spotlight control remains routed through the existing control-room attendee and raised-hand seams.",
    proof: "Hand-raise queue, attendee shadow controls, and spotlight state remain mounted in the control room and are distinct from presenter-management roster authorization. The browser proof exercises raised-hand spotlight, lower-hand queue removal, and explicit remove-spotlight teardown directly from the attendee queue before moving on to slide workflow verification.",
  },
  {
    surface: "lower thirds",
    state: "disabled",
    detail: "Lower thirds remain disabled until a program-output compositor seam is mounted for webinar live output.",
    proof: "No compositor-backed lower-third runtime is mounted on this branch, so this surface must stay explicitly disabled.",
  },
  {
    surface: "agenda and countdown",
    state: "disabled",
    detail: "Agenda and countdown remain disabled because timed rundown state is not mounted in the control room runtime.",
    proof: "No timed rundown or live countdown route is mounted on this branch, so agenda/countdown controls remain explicitly disabled.",
  },
  {
    surface: "resource links",
    state: "disabled",
    detail: "Resource links remain disabled until live attendee delivery exists.",
    proof: "No attendee-side live link injection seam is mounted from the control room on this branch.",
  },
  {
    surface: "breakout timers",
    state: "live",
    detail: "Breakout timers are reachable from the mounted webinar management route.",
    proof: `${BREAKOUT_TIMERS_ROUTE_PATH} mounts the host breakout setup, start, active monitoring, and timer workflow through BreakoutRoomsManagementPage.`,
  },
  {
    surface: "camera and mic preview",
    state: "disabled",
    detail: "Camera and mic preview stays explicitly disabled in the control room because no inline device-preview control is mounted on this surface.",
    proof: "Presenters must use the dedicated presenter preview/device-check browser surface; this control room only records that handoff and must not claim an inline live control that is not mounted here.",
  },
  {
    surface: "backstage",
    state: "disabled",
    detail: "Backstage stays explicitly disabled in the control room because no inline backstage staging control is mounted on this surface.",
    proof: "Presenters must use the dedicated backstage presenter/browser surface; this control room only records that handoff and must not claim an inline live control that is not mounted here.",
  },
  {
    surface: "co-host permissions",
    state: "disabled",
    detail: "Co-host production permissions stay explicitly disabled in the control room because this surface does not mount a working inline permission grant flow.",
    proof: "Manager-authorized roster changes remain separate, and co-presenter browser/runtime handoff still belongs to dedicated presenter/browser surfaces; this control room must not claim a live inline permission control it does not own.",
  },
  {
    surface: "screen share",
    state: "disabled",
    detail: "Screen share stays explicitly disabled in the control room because no inline capture negotiation control is mounted on this surface.",
    proof: "Presenters must use the dedicated presenter/browser capture flow; this control room only records that handoff and must not claim an inline live control that is not mounted here.",
  },
] as const;

const CONTROL_ROOM_BROWSER_SURFACE_PROOF = {
  cameraMicPreview: "Disabled in control room: no inline device-preview control is mounted here, and presenters must use the dedicated presenter/browser preview surface.",
  backstage: "Disabled in control room: no inline backstage staging control is mounted here, and presenters must use the dedicated backstage presenter/browser surface.",
  coHostPermissions: "Disabled in control room: no inline co-host permission grant flow is mounted here, and operators must use manager-authorized roster changes plus dedicated presenter/browser handoff surfaces.",
  screenShare: "Disabled in control room: no inline screen-share capture negotiation control is mounted here, and presenters must use the dedicated presenter/browser capture surface.",
} as const;

const CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY = {
  presenterManagement: "Presenter-management keeps its existing manager-authorized response shape and does not reinterpret presenter roster labels as presenter-management API grants.",
  productionControls: "Presenter-management stays manager-authorized, slide mutations keep their separate route behavior without being reinterpreted from presenter roster labels, and layout/spotlight seams keep their own backend contracts. This control-room proof does not claim co-presenter, moderator, or guest-speaker mutation rights on route-specific production controls, and it does not reinterpret presenter-management roster labels as route authorization.",
  browserNegotiation: "Browser device-preview, backstage, and screen-share negotiation stay outside presenter-management ownership on this branch and remain explicitly disabled in the control room until dedicated inline controls are mounted here.",
} as const;

// Lineage proof: 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1
// Generated work: 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:WEB-100-006-presenter-production-tools
export const CONTROL_ROOM_EXECUTION_PROOF = {
  packetId: ACTIVE_PACKET_ID,
  generatedWorkItem: GENERATED_WORK_ITEM,
  antiRequirementProof: {
    noPresenterManagementPermissionChange: CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.presenterManagement,
    noImplicitProductionControlGrant: CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.productionControls,
    unsupportedBrowserNegotiationStaysDisabled: CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.browserNegotiation,
  },
  evidence: [
    "current webinar vision gap map",
    "route proof",
    "frontend/browser proof",
    "state-store proof",
    "failure-mode proof",
    "control-room browser proof",
    "layout switching proof",
    "presenter permission proof",
    "browser-surface disposition proof",
  ],
  routeProofFiles: [
    "apps/ease-experience/src/routes/webinars/control-room.tsx",
    "apps/ease-experience/src/routes/webinars/index.tsx",
    "apps/ease-experience/src/routes/webinars/breakout-rooms.tsx",
    "workers/api-webinar/src/routes/slides.ts",
    "workers/api-webinar/src/routes/presenter-management.ts",
  ],
  currentWebinarVisionGapMap: CONTROL_ROOM_CURRENT_WEBINAR_VISION_GAP_MAP,
  routeProof: [
    "GET /v1/webinars/:webinarId/control-room/stats",
    "GET /v1/webinars/:webinarId/control-room/program-layout",
    "POST /v1/webinars/:webinarId/control-room/layout",
    "GET /v1/webinars/:webinarId/control-room/layout/presets",
    "POST /v1/webinars/:webinarId/slides/upload",
    "PUT /v1/webinars/:webinarId/slides",
    "POST /v1/webinars/:webinarId/slides/current",
    "GET /v1/webinars/:webinarId/presenter-management/presenters",
    "GET /webinars/:webinarId/breakouts",
  ],
  stateStoreProof: {
    layoutSwitching: [
      "programLayout.currentLayout plus runtime revision/updatedAt freshness read from program-output runtime after switch request",
      "layoutState.preset stored preset reference",
      "requestedLayoutPreset pending operator intent",
    ],
    slides: [
      "mediaUploads rows plus R2 slide assets via POST /slides/upload",
      "webinars.streamConfig.controlRoom.slides via PUT /slides and POST /slides/current",
      "webinar.slide.changed realtime topic emitted by PUT /slides and POST /slides/current",
    ],
    presenters: [
      "webinars.streamConfig.presenterManagement.presenters",
      "manager-only presenter-management route contract",
    ],
  },
  presenterAuthorizationModelProof: {
    presenterManagement: "Presenter-management proof is limited to its existing manager-only backend contract and does not claim viewer-permission, roster-label, or browser-negotiation grants.",
    slideMutations: "Slide routes prove mounted deck/current-slide behavior without introducing new presenter-management role gates on this branch; presenter roster labels still are not treated as slide-route authorization, and the control room now mounts that existing slide workflow while keeping unsupported overlay/link surfaces explicitly disabled. Breakout timers are handled by the mounted webinar breakout management route.",
    layoutMutations: "Layout proof in this control-room route is limited to live runtime confirmation and does not assert co-presenter, moderator, or guest-speaker mutation grants for route-specific layout controls.",
  },
  layoutSwitchingProof: "Layout preset requests stay live whenever /control-room/program-layout reports runtimeAvailable=true. Active highlighting still follows refreshed programLayout.currentLayout from the program-output runtime seam after each switch request, and the requested preset stays pending until runtime freshness confirms that telemetry was refreshed after the request. When revision/updatedAt metadata is available, it must advance; otherwise the UI waits for a post-request runtime poll cycle before marking the layout live. Stored preset state never marks a preset live by itself.",
  runtimeLayoutProof: "/control-room/program-layout reads currentLayout plus runtimeAvailable from the webinar program-output runtime seam; persisted layout is exposed separately as storedLayout and cannot mark a preset live by itself.",
  browserExecutionProof: "Mounted control-room status cards keep preview, backstage, co-host permissions, and screen share as explicit disabled handoffs to dedicated presenter/browser surfaces while exposing a live breakout-timers link to the mounted webinar breakout management route. The attendee queue browser proof exercises spotlight handoff, lower-hand queue removal, and explicit remove-spotlight teardown against the live control-room hand-raise routes before the mounted slides panel drives upload/save/current-slide controls, waits for the refreshed two-slide deck to render before live navigation advances, and confirms that layout telemetry badges distinguish live runtime state from stored preset state.",
  presenterPermissionProof: `${CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.presenterManagement} ${CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.productionControls} ${CONTROL_ROOM_PRESENTER_PERMISSION_BOUNDARY.browserNegotiation}`,
  browserHarnessProof: "The browser harness asserts the rendered control-room surface directly: layout telemetry badges prove live-output confirmation, the attendee queue drives spotlight handoff, lower-hand queue removal, and explicit remove-spotlight teardown through the mounted hand-raise controls, the mounted slides panel drives upload/save/current-slide controls against the live slide routes and waits for the refreshed deck outline to expose Next before advancing, breakout timers link to the mounted webinar breakout route, and preview/backstage/co-host/screen-share remain explicit disabled handoffs instead of false inline controls.",
  browserProof: CONTROL_ROOM_BROWSER_SURFACE_PROOF,
  failureModeProof: {
    layoutTelemetry: "Preset requests remain in an awaiting-confirmation state until refreshed runtime telemetry reports the requested program layout. When runtime freshness metadata is available it must advance after the request; otherwise the UI waits for a post-request runtime poll cycle before clearing the pending state.",
    preview: "Preview/device checks are disabled in the control room and must hand off to the dedicated presenter preview surface; if that handoff path is unavailable, the disabled reason remains accurate instead of implying a working inline control.",
    backstage: "Backstage controls are disabled in the control room and must hand off to the dedicated backstage surface; if that handoff path is unavailable, the disabled reason remains accurate instead of implying a working inline control.",
    coHostPermissions: "Inline co-host permissions stay disabled in the control room; manager-authorized roster changes remain separate, and failure on dedicated presenter/browser handoff surfaces does not become a false inline success state.",
    screenShare: "Screen-share capture is disabled in the control room and must hand off to the dedicated presenter/browser surface; if that handoff path is unavailable, the disabled reason remains accurate instead of implying a working inline control.",
  },
  disabledSurfaces: {
    lowerThirds: "Lower thirds stay disabled until a dedicated program-output compositor is mounted on the webinar runtime.",
    agendaCountdown: "Agenda and countdown controls stay disabled because no timed rundown seam is mounted from the control room.",
    resourceLinks: "Resource links stay disabled until a live attendee delivery route exists for webinar-side link injections.",
  },
  mountedSurfaces: {
    breakoutTimers: `${BREAKOUT_TIMERS_ROUTE_PATH} mounts BreakoutRoomsManagementPage so hosts can set up, start, monitor, and end timed breakout sessions from the webinar management module.`,
  },
} as const;

const DEFAULT_MODERATION_STATE: ChatModerationState = {
  settings: {
    bannedWords: [],
    rateLimitPerMinute: 10,
    linkPolicy: "hold",
    emojiFloodMax: 8,
    capsRatioMax: 0.75,
    duplicateWindowSeconds: 30,
    homoglyphNormalize: true,
    zeroWidthStrip: true,
    zalgoStripDepth: 3,
    raidJoinsPerMinuteThreshold: 50,
    raidAction: "hold",
    classifierFallback: "hold",
    holdQueueEnabled: true,
  },
  participants: [],
  queue: [],
  actions: [],
  reports: [],
  raid: {
    enabled: false,
    action: "hold",
    triggeredAt: null,
    expiresAt: null,
    joinsObserved: 0,
  },
};

interface LocalPresenterMessage {
  localId: string;
  serverMessageId?: string;
  senderName: string;
  senderRole: "presenter";
  text: string;
  timestamp: string;
  senderId?: string;
  isPinned?: boolean;
  shadowFiltered?: boolean;
  moderationCategory?: string | null;
  status: ComposerStatus;
}

function createEmptySpotlightState(): SpotlightState {
  return {
    participantId: null,
    displayName: null,
    updatedAt: null,
  };
}

export default function ControlRoomPage() {
  const { webinarId } = useParams();
  const [activePanel, setActivePanel] = useState<ActivePanel>("chat");
  const [requestedLayoutPreset, setRequestedLayoutPreset] = useState<LayoutPreset | null>(null);
  const [layoutConfirmationRequestedAt, setLayoutConfirmationRequestedAt] = useState<number | null>(null);
  const [layoutConfirmationPollCount, setLayoutConfirmationPollCount] = useState(0);
  const [requestedLayoutBaselineRevision, setRequestedLayoutBaselineRevision] = useState<number | null>(null);
  const [requestedLayoutBaselineUpdatedAt, setRequestedLayoutBaselineUpdatedAt] = useState<string | null>(null);
  const [layoutSwitchFailure, setLayoutSwitchFailure] = useState<{ preset: LayoutPreset; message: string } | null>(null);

  const { data: stats, error: statsError, refetch: refetchStats } = useControlRoomStats(webinarId);
  const { data: programLayout, error: programLayoutError, refetch: refetchProgramLayout } = useProgramLayoutRuntime(webinarId);
  const { data: layoutState, refetch: refetchLayout } = useControlRoomLayout(webinarId);
  const { data: layoutPresetData } = useLayoutPresets(webinarId);
  const {
    mutate: switchLayout,
    isLoading: isSwitchingLayout,
    error: layoutSwitchError,
    data: switchedLayoutState,
  } = useSwitchLayout(webinarId);
  const { data: engagement } = useEngagement(webinarId);
  const { data: suggestions } = useCopilotSuggestions(webinarId);
  const chatQuery = useChatMessages(webinarId);
  const { data: chat } = chatQuery;
  const { data: moderation } = useChatModeration(webinarId);
  const { data: questions } = useQaQuestions(webinarId);
  const { data: attendees } = useAttendees(webinarId);
  const { data: handRaiseState, lowerHand, lowerAllHands, spotlightParticipant, removeSpotlight } = useHandRaiseQueue(webinarId);
  const pollManager = usePollManager(webinarId);
  const sendChatMessage = useSendChatMessage(webinarId);
  const muteParticipant = useMuteParticipant(webinarId);
  const banParticipant = useBanParticipant(webinarId);
  const shadowParticipant = useShadowParticipant(webinarId);
  const updateModerationSettings = useUpdateChatModerationSettings(webinarId);
  const refetchProgramLayoutRef = useRef(refetchProgramLayout);

  useEffect(() => {
    refetchProgramLayoutRef.current = refetchProgramLayout;
  }, [refetchProgramLayout]);

  const elapsed = stats?.elapsedSeconds ?? 0;
  const elapsedStr = `${Math.floor(elapsed / 3600)}:${String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const persistedLayoutPreset = ((switchedLayoutState?.preset ?? programLayout?.storedLayout ?? layoutState?.preset) as LayoutPreset | undefined) ?? null;
  const liveLayoutPreset = (programLayout?.currentLayout as LayoutPreset | undefined) ?? null;
  const liveLayoutRevision = extractRuntimeRevision(programLayout);
  const liveLayoutUpdatedAt = extractRuntimeUpdatedAt(programLayout);
  const liveLayoutUpdatedAtMs = parseTimestampMs(liveLayoutUpdatedAt);
  const requestedLayoutBaselineUpdatedAtMs = parseTimestampMs(requestedLayoutBaselineUpdatedAt);
  const hasLayoutRuntime = programLayout?.runtimeAvailable === true;
  const hasLiveLayoutTelemetry = hasLayoutRuntime && liveLayoutPreset !== null;
  const layoutSwitchDisabledReason = hasLayoutRuntime
    ? null
    : programLayout?.runtimeAvailable === false
      ? programLayout.runtimeUnavailableReason ?? "Program output telemetry is unavailable, so stored-only layout changes are disabled."
      : "Program output runtime availability has not reported yet, so layout switching stays disabled until the live output seam is ready.";
  const canSwitchLiveLayout = hasLayoutRuntime && !isSwitchingLayout;
  const layoutPresetPalette: LayoutPresetPaletteItem[] = [
    ...(layoutPresetData?.presets ?? []),
    ...((layoutPresetData?.unsupportedVisionPresets ?? []) as UnsupportedVisionLayoutPresetPaletteItem[]),
  ];
  const layoutPresetLookup = useMemo(
    () => new Map((layoutPresetData?.presets ?? []).map((preset) => [preset.id, preset.label])),
    [layoutPresetData?.presets],
  );
  const describeLayoutPreset = (presetId: LayoutPreset | null) => {
    if (!presetId) {
      return "Waiting for telemetry";
    }

    return layoutPresetLookup.get(presetId) ?? presetId.replace(/_/g, " ");
  };
  const runtimeTelemetryRevisionAdvanced = requestedLayoutBaselineRevision !== null
    && liveLayoutRevision !== null
    && liveLayoutRevision > requestedLayoutBaselineRevision;
  const runtimeTelemetryUpdatedAfterBaseline = requestedLayoutBaselineUpdatedAtMs !== null
    && liveLayoutUpdatedAtMs !== null
    && liveLayoutUpdatedAtMs > requestedLayoutBaselineUpdatedAtMs;
  const runtimeTelemetryUpdatedAfterRequest = layoutConfirmationRequestedAt !== null
    && liveLayoutUpdatedAtMs !== null
    && liveLayoutUpdatedAtMs >= layoutConfirmationRequestedAt;
  const canCheckRuntimeFreshness = requestedLayoutPreset !== null
    && (
      requestedLayoutBaselineRevision !== null
      || requestedLayoutBaselineUpdatedAt !== null
      || liveLayoutRevision !== null
      || liveLayoutUpdatedAt !== null
    );
  const hasPostRequestRuntimePoll = layoutConfirmationPollCount > 0;
  const displayedHandRaiseState = {
    queue: handRaiseState?.queue ?? [],
    spotlight: handRaiseState?.spotlight ?? createEmptySpotlightState(),
  };
  const hasFreshRequestedLayoutConfirmation = requestedLayoutPreset !== null
    && liveLayoutPreset === requestedLayoutPreset
    && (
      canCheckRuntimeFreshness
        ? runtimeTelemetryRevisionAdvanced
          || runtimeTelemetryUpdatedAfterBaseline
          || runtimeTelemetryUpdatedAfterRequest
        : hasPostRequestRuntimePoll
    );
  const isAwaitingLiveLayoutConfirmation = requestedLayoutPreset !== null && !hasFreshRequestedLayoutConfirmation;
  const resetLayoutConfirmationState = () => {
    setRequestedLayoutPreset(null);
    setLayoutConfirmationRequestedAt(null);
    setLayoutConfirmationPollCount(0);
    setRequestedLayoutBaselineRevision(null);
    setRequestedLayoutBaselineUpdatedAt(null);
  };
  // Packet 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1
  // Generated work 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:WEB-100-006-presenter-production-tools
  const controlSurfaceCards: ControlSurfaceCard[] = [
    {
      key: "layout-switching",
      label: "Layout switching",
      state: hasLayoutRuntime ? "live" : "disabled",
      summary: hasLiveLayoutTelemetry
        ? `Program telemetry reports ${describeLayoutPreset(liveLayoutPreset)}.`
        : hasLayoutRuntime
          ? "Live switch requests are enabled while runtime telemetry catches up with the active preset."
          : layoutSwitchDisabledReason ?? "Program layout runtime is unavailable.",
      detail: hasLayoutRuntime
        ? "Preset highlighting follows refreshed runtime telemetry instead of stored preset state, and runtime freshness confirmation requires either newer currentLayoutRevision/currentLayoutUpdatedAt metadata or, when that metadata is absent on the frontend seam, a post-request runtime poll before the active program layout is treated as switched."
        : `${layoutSwitchDisabledReason ?? "Program layout runtime is unavailable."} Stored preset changes stay blocked until refreshed runtime telemetry reports the active preset.`,
      actionLabel: hasLiveLayoutTelemetry ? "Program telemetry confirmed" : hasLayoutRuntime ? "Switch route live" : "Telemetry required",
    },
    {
      key: "slides",
      label: "Slide deck import and deck creation",
      state: "live",
      summary: "The mounted slides workflow keeps deck upload, persistence, and live current-slide switching available from the control room.",
      detail: `${SLIDE_ROUTE_PROOF.upload} ${SLIDE_ROUTE_PROOF.deckPersistence} ${SLIDE_ROUTE_PROOF.currentSlide} ${SLIDE_UNSUPPORTED_CONTROLS_REASON}`,
      actionLabel: "Slides workflow live",
    },
    {
      key: "spotlight",
      label: "Spotlight handoff",
      state: "live",
      summary: "Raised-hand spotlight controls stay active from the attendee queue panel.",
      detail: "Spotlight, lower-hand, and remove-spotlight actions remain wired through the hand-raise queue seam so a host can move an attendee into the live spotlight, clear queue entries independently, and end spotlight explicitly.",
      actionLabel: "Spotlight live",
    },
    {
      key: "permissions",
      label: "Co-host permissions",
      state: "disabled",
      summary: "Inline co-host permission changes are not mounted on this control-room surface.",
      detail: "Manager-authorized roster changes remain separate, and co-host production/browser handoff still belongs to dedicated presenter/browser surfaces.",
      actionLabel: "Use presenter surface",
    },
    {
      key: "lower-thirds",
      label: "Lower thirds",
      state: "disabled",
      summary: "No lower-third compositor is mounted on the webinar runtime yet.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.disabledSurfaces.lowerThirds,
      actionLabel: "Lower thirds unavailable",
    },
    {
      key: "agenda-countdown",
      label: "Agenda and countdown",
      state: "disabled",
      summary: "Timed rundown controls are not mounted from the control room yet.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.disabledSurfaces.agendaCountdown,
      actionLabel: "Agenda unavailable",
    },
    {
      key: "resource-links",
      label: "Resource links",
      state: "disabled",
      summary: "Attendee-facing link injection is not wired from this control room build.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.disabledSurfaces.resourceLinks,
      actionLabel: "Resource links unavailable",
    },
    {
      key: "breakout-timers",
      label: "Breakout timers",
      state: "live",
      summary: "Breakout timers are reachable from the mounted webinar management route.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.mountedSurfaces.breakoutTimers,
      actionLabel: "Open breakout timers",
      actionHref: webinarId ? `/webinars/${webinarId}/breakouts` : undefined,
    },
    {
      key: "camera-mic-preview",
      label: "Camera and mic preview",
      state: "disabled",
      summary: "No inline camera/mic preview control is mounted in the control room.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.browserProof.cameraMicPreview,
      actionLabel: "Use presenter preview",
    },
    {
      key: "backstage",
      label: "Backstage",
      state: "disabled",
      summary: "No inline backstage staging control is mounted in the control room.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.browserProof.backstage,
      actionLabel: "Use backstage surface",
    },
    {
      key: "screen-share",
      label: "Screen share",
      state: "disabled",
      summary: "No inline screen-share capture control is mounted in the control room.",
      detail: CONTROL_ROOM_EXECUTION_PROOF.browserProof.screenShare,
      actionLabel: "Use presenter capture",
    },
  ];

  useEffect(() => {
    if (requestedLayoutPreset && hasFreshRequestedLayoutConfirmation) {
      resetLayoutConfirmationState();
    }
  }, [hasFreshRequestedLayoutConfirmation, requestedLayoutPreset]);

  useEffect(() => {
    if (!requestedLayoutPreset || !isAwaitingLiveLayoutConfirmation) {
      return;
    }

    const requestedAt = layoutConfirmationRequestedAt ?? Date.now();
    if (layoutConfirmationRequestedAt === null) {
      setLayoutConfirmationRequestedAt(requestedAt);
    }
    const requestedLayoutLabel =
      layoutPresetLookup.get(requestedLayoutPreset) ?? requestedLayoutPreset.replace(/_/g, " ");

    const pollRuntimeLayout = () => {
      if (Date.now() - requestedAt >= LAYOUT_CONFIRMATION_TIMEOUT_MS) {
        setLayoutSwitchFailure({
          preset: requestedLayoutPreset,
          message: `Runtime telemetry did not confirm ${requestedLayoutLabel} within ${LAYOUT_CONFIRMATION_TIMEOUT_MS / 1_000}s.`,
        });
        resetLayoutConfirmationState();
        return;
      }

      setLayoutConfirmationPollCount((count) => count + 1);
      refetchProgramLayoutRef.current();
    };

    const intervalId = window.setInterval(pollRuntimeLayout, LAYOUT_CONFIRMATION_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isAwaitingLiveLayoutConfirmation, layoutConfirmationRequestedAt, layoutPresetLookup, requestedLayoutPreset]);

  const handleLayoutSwitch = (preset: LayoutPreset) => {
    if (!hasLayoutRuntime) {
      setLayoutSwitchFailure({
        preset,
        message: layoutSwitchDisabledReason ?? "Program output telemetry is unavailable. Layout switching stays disabled until live output can be confirmed.",
      });
      return;
    }

    if (isSwitchingLayout) {
      return;
    }

    setRequestedLayoutPreset(preset);
    setLayoutConfirmationRequestedAt(Date.now());
    setLayoutConfirmationPollCount(0);
    setRequestedLayoutBaselineRevision(liveLayoutRevision);
    setRequestedLayoutBaselineUpdatedAt(liveLayoutUpdatedAt);
    setLayoutSwitchFailure(null);
    void Promise.resolve(switchLayout({ preset, transition: "crossfade" })).then((switchResult) => {
      const nextLayout: ControlRoomLayoutSwitchResult | null = isLayoutSwitchMutationResult(switchResult)
        ? switchResult.ok ? switchResult.layout : null
        : switchResult;
      const failureMessage = isLayoutSwitchMutationResult(switchResult) && !switchResult.ok
        ? switchResult.error
        : layoutSwitchError;

      if (nextLayout !== null) {
        refetchStats();
        refetchProgramLayout();
        refetchLayout();
        return;
      }

      resetLayoutConfirmationState();
      setLayoutSwitchFailure({
        preset,
        message: failureMessage ?? `Failed to request ${describeLayoutPreset(preset)}. Program output was not changed.`,
      });
    }).catch((error: unknown) => {
      resetLayoutConfirmationState();
      setLayoutSwitchFailure({
        preset,
        message: getLayoutSwitchFailureMessage(
          error,
          `Failed to request ${describeLayoutPreset(preset)}. Program output was not changed.`,
        ),
      });
    });
  };

  const handleSpotlightAction = async (participantId: string) => {
    return spotlightParticipant(participantId);
  };

  const handleLowerHandAction = async (participantId: string) => {
    return lowerHand(participantId);
  };

  const handleLowerAllHandsAction = async () => {
    return lowerAllHands();
  };

  const handleRemoveSpotlightAction = async (participantId: string) => {
    return removeSpotlight(participantId);
  };

  return (
    <div className="flex flex-col h-screen bg-[var(--color-background)]">
      {/* Error banner */}
      {(statsError || programLayoutError) && (
        <div className="px-4 py-2 bg-[var(--color-destructive)]/5 border-b border-[var(--color-destructive)]/20 text-sm text-[var(--color-destructive)] flex items-center justify-between">
          <span>Failed to load control room data: {statsError ?? programLayoutError}</span>
          <button
            onClick={() => {
              refetchStats();
              refetchProgramLayout();
            }}
            className="underline ml-2"
          >
            Retry
          </button>
        </div>
      )}
      {/* Top bar — stats + engagement meter */}
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2 bg-[var(--color-card)]">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Radio className="h-4 w-4 text-[var(--color-destructive)] animate-pulse" />
            <span className="text-sm font-bold text-[var(--color-destructive)]">LIVE</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-[var(--color-muted-foreground)]">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-mono">{elapsedStr}</span>
          </div>
          <StatBadge icon={Users} label="Attendees" value={stats?.attendeeCount ?? 0} />
          <StatBadge icon={MessageSquare} label="Chat" value={stats?.chatCount ?? 0} />
          <StatBadge icon={HelpCircle} label="Q&A" value={stats?.qaQueueCount ?? 0} />
        </div>

        {/* Engagement meter */}
        <EngagementMeter data={engagement} />
      </div>

      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
          <LayoutTemplate className="h-3.5 w-3.5" />
          Layout Presets
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusPill
            testId="program-layout-status"
            tone={hasLayoutRuntime ? (hasLiveLayoutTelemetry ? "success" : "warning") : "disabled"}
            label={`Program telemetry: ${describeLayoutPreset(liveLayoutPreset)}`}
          />
          <StatusPill
            testId="stored-layout-status"
            tone={persistedLayoutPreset === liveLayoutPreset ? "default" : "warning"}
            label={`Stored preset: ${describeLayoutPreset(persistedLayoutPreset)}`}
          />
          {isAwaitingLiveLayoutConfirmation ? (
            <StatusPill
              testId="layout-confirmation-status"
              tone="warning"
              label={`Awaiting runtime confirmation: ${describeLayoutPreset(requestedLayoutPreset)}`}
            />
          ) : null}
          {layoutSwitchFailure ? (
            <StatusPill
              testId="layout-switch-error"
              tone="danger"
              label={`Layout switch failed: ${describeLayoutPreset(layoutSwitchFailure.preset)}`}
            />
          ) : null}
        </div>
        {layoutSwitchFailure ? (
          <div
            data-testid="layout-switch-error-detail"
            className="mt-3 rounded-2xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 px-3 py-2 text-xs leading-5 text-[var(--color-destructive)]"
          >
            {layoutSwitchFailure.message}
          </div>
        ) : null}
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Active highlighting follows refreshed program telemetry before stored state, and a requested preset stays pending until runtime freshness confirms the live feed changed after the operator action. When revision metadata is absent on this frontend seam, the UI still waits for a post-request runtime poll before confirming the switch.
        </p>
        {layoutSwitchDisabledReason ? (
          <div
            data-testid="layout-switch-disabled-reason"
            className="mt-3 rounded-2xl border border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 px-3 py-2 text-xs leading-5 text-[var(--color-warning)]"
          >
            {layoutSwitchDisabledReason}
          </div>
        ) : null}
        <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
          {layoutPresetPalette.map((preset) => {
            const isRunnablePreset = preset.liveOutputSupported === true;
            const isLive = liveLayoutPreset === preset.id;
            const isPersisted = persistedLayoutPreset === preset.id;
            const isRequested = requestedLayoutPreset === preset.id && isAwaitingLiveLayoutConfirmation;
            const presetDisabledReason = isRunnablePreset ? layoutSwitchDisabledReason : preset.disabledReason;
            const canSwitchPreset = isRunnablePreset && canSwitchLiveLayout;
            return (
              <button
                key={preset.id}
                type="button"
                data-testid={`layout-preset-${preset.id}`}
                disabled={!canSwitchPreset}
                onClick={() => {
                  if (isRunnablePreset) {
                    handleLayoutSwitch(preset.id);
                  }
                }}
                className={`rounded-2xl border px-3 py-3 text-left transition ${
                  !isRunnablePreset
                    ? "cursor-not-allowed border-dashed border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 text-[var(--color-muted-foreground)] opacity-80"
                    : !hasLayoutRuntime
                    ? "cursor-not-allowed border-dashed border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 text-[var(--color-muted-foreground)] opacity-70"
                    : isLive
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]"
                    : isRequested
                      ? "border-[var(--color-warning)] bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                    : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-card)]/80"
                }`}
                title={presetDisabledReason ?? undefined}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="rounded-xl bg-[var(--color-muted)]/40 p-2 text-[var(--color-foreground)]">
                    <MonitorPlay className="h-4 w-4" />
                  </div>
                  <span className="rounded-full border border-current/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
                    {preset.shortcut ?? "Vision"}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{preset.label}</span>
                  {isLive && isRunnablePreset ? (
                    <span className="rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary-foreground)]">
                      Live
                    </span>
                  ) : null}
                  {!isLive && isPersisted && isRunnablePreset ? (
                    <span className="rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-warning)]">
                      Stored
                    </span>
                  ) : null}
                  {isRequested && isRunnablePreset ? (
                    <span className="rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-warning)]">
                      Requested
                    </span>
                  ) : null}
                  {!isRunnablePreset || !hasLayoutRuntime ? (
                    <span className="rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-warning)]">
                      Disabled
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">{preset.description}</div>
                {!isRunnablePreset ? (
                  <div
                    data-testid={`layout-preset-disabled-reason-${preset.id}`}
                    className="mt-2 text-xs leading-5 text-[var(--color-warning)]"
                  >
                    {preset.disabledReason}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {controlSurfaceCards.map((card) => (
            <div
              key={card.key}
              data-testid={`control-surface-${card.key}`}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[var(--color-foreground)]">{card.label}</p>
                  <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">{card.summary}</p>
                </div>
                <SurfaceStateBadge state={card.state} />
              </div>
              {card.state === "disabled" ? (
                <>
                  <button
                    type="button"
                    data-testid={`control-surface-action-${card.key}`}
                    disabled
                    className="mt-3 w-full rounded-xl border border-dashed border-[var(--color-border)] px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)] opacity-70"
                    title={card.detail}
                  >
                    {card.actionLabel}
                  </button>
                  <p
                    data-testid={`control-surface-reason-${card.key}`}
                    className="mt-2 text-xs leading-5 text-[var(--color-muted-foreground)]"
                  >
                    {card.detail}
                  </p>
                </>
              ) : (
                card.actionHref ? (
                  <a
                    href={card.actionHref}
                    data-testid={`control-surface-action-${card.key}`}
                    className="mt-3 block rounded-xl bg-[var(--color-primary-light)] px-3 py-2 text-xs font-semibold text-[var(--color-primary)]"
                  >
                    {card.actionLabel}
                  </a>
                ) : (
                  <div
                    data-testid={`control-surface-action-${card.key}`}
                    className="mt-3 rounded-xl bg-[var(--color-primary-light)] px-3 py-2 text-xs text-[var(--color-primary)]"
                  >
                    {card.actionLabel}
                  </div>
                )
              )}
              <p className="mt-2 text-xs leading-5 text-[var(--color-muted-foreground)]">{card.detail}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-[var(--color-muted-foreground)]">
          Slide deck actions stay mounted through the slides workflow below. Browser-only presenter tools remain disabled here until dedicated preview, backstage, co-host permission, and screen-share controls are mounted on this surface.
        </p>
      </div>

      {/* Main content — panels */}
      <div className="flex flex-1 overflow-hidden">
        {/* Panel navigation */}
        <div className="w-14 border-r border-[var(--color-border)] bg-[var(--color-surface)] flex flex-col items-center py-2 gap-1">
          {([
            { key: "chat" as const, Icon: MessageSquare, label: "Chat" },
            { key: "moderation" as const, Icon: ShieldAlert, label: "Moderation" },
            { key: "qa" as const, Icon: HelpCircle, label: "Q&A" },
            { key: "polls" as const, Icon: BarChart3, label: "Polls" },
            { key: "attendees" as const, Icon: Users, label: "Attendees" },
            { key: "slides" as const, Icon: Presentation, label: "Slides" },
            { key: "canvas" as const, Icon: PenLine, label: "Canvas" },
            { key: "captions" as const, Icon: Volume2, label: "Captions" },
            { key: "cta" as const, Icon: ShoppingBag, label: "CTAs" },
            { key: "copilot" as const, Icon: Brain, label: "Co-Pilot" },
          ]).map(({ key, Icon, label }) => (
            <button
              key={key}
              onClick={() => setActivePanel(key)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                activePanel === key
                  ? key === "copilot"
                    ? "bg-[var(--color-ai-light)] text-[var(--color-ai)]"
                    : "bg-[var(--color-primary-light)] text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
              }`}
              aria-label={label}
              title={label}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>

        {/* Active panel content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activePanel === "chat" && (
            <ChatPanel
              messages={chat ?? []}
              moderation={moderation ?? DEFAULT_MODERATION_STATE}
              sendError={sendChatMessage.error}
              sendMessage={async (content) => {
                const sent = await sendChatMessage.mutate(content);
                if (sent) {
                  chatQuery.refetch();
                }
                return sent;
              }}
              onMute={(participantId) => void muteParticipant.mutate(participantId)}
              onBan={(participantId) => void banParticipant.mutate(participantId)}
              onShadow={(participantId) => void shadowParticipant.mutate(participantId)}
              onSaveSettings={(settings) => void updateModerationSettings.mutate(settings)}
            />
          )}
          {activePanel === "moderation" && webinarId && (
            <div className="h-full overflow-y-auto p-4">
              <ModerationDashboard webinarId={webinarId} />
            </div>
          )}
          {activePanel === "qa" && <QaPanel questions={questions ?? []} />}
          {activePanel === "polls" && (
            <PollsPanel
              polls={pollManager.data ?? []}
              resultsById={pollManager.resultsById}
              error={pollManager.error}
              onCreateAndLaunch={pollManager.createAndLaunch}
              onLaunch={pollManager.launchPoll}
              onClose={pollManager.closePoll}
            />
          )}
          {activePanel === "attendees" && (
            <AttendeesPanel
              attendees={attendees ?? []}
              queue={displayedHandRaiseState.queue}
              spotlight={displayedHandRaiseState.spotlight}
              onLowerHand={handleLowerHandAction}
              onLowerAllHands={handleLowerAllHandsAction}
              onSpotlight={handleSpotlightAction}
              onRemoveSpotlight={handleRemoveSpotlightAction}
            />
          )}
          {activePanel === "slides" && (
            <div className="h-full overflow-y-auto p-4">
              <div
                data-testid="slides-panel-mounted-state"
                className="rounded-2xl border border-[var(--color-primary)]/20 bg-[var(--color-primary-light)]/60 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">
                      Slides workflow live
                    </p>
                    <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">
                      Deck upload, deck save, deck-outline refresh, and live current-slide switching stay available through the mounted slides workflow. Lower thirds, agenda/countdown, and resource links remain disabled above until their dedicated live-output seams are mounted; breakout timers route to the mounted webinar breakout management page.
                    </p>
                  </div>
                  <SurfaceStateBadge state="live" />
                </div>
              </div>
              <div data-testid="slides-panel-mounted" className="mt-4">
                <SlidesPanel webinarId={webinarId} compact={true} />
              </div>
            </div>
          )}
          {activePanel === "canvas" && webinarId && (
            <div className="p-4 h-full">
              <CanvasRenderer webinarId={webinarId} mode="presenter" />
            </div>
          )}
          {activePanel === "captions" && webinarId && (
            <div className="p-4 h-full overflow-y-auto bg-gray-950">
              <CaptionPresenterControls webinarId={webinarId} />
            </div>
          )}
          {activePanel === "cta" && <CtaPanel webinarId={webinarId} />}
          {activePanel === "copilot" && <CopilotPanel suggestions={suggestions ?? []} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Badge
// ---------------------------------------------------------------------------

function StatBadge({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5 text-sm" title={label}>
      <Icon className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
      <span className="font-medium text-[var(--color-foreground)]">{formatNumber(value)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Engagement Meter
// ---------------------------------------------------------------------------

function EngagementMeter({ data }: { data: EngagementData | null }) {
  const score = data?.score ?? 0;
  const color = score >= 70 ? "text-[var(--color-success)]"
    : score >= 40 ? "text-[var(--color-warning)]"
    : "text-[var(--color-destructive)]";
  const barColor = score >= 70 ? "bg-[var(--color-success)]"
    : score >= 40 ? "bg-[var(--color-warning)]"
    : "bg-[var(--color-destructive)]";

  return (
    <div className="flex items-center gap-3">
      {data?.warning && (
        <span className="text-xs text-[var(--color-warning)] flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {data.warning}
        </span>
      )}
      <div className="flex items-center gap-2">
        <Gauge className="h-4 w-4 text-[var(--color-muted-foreground)]" />
        <div className="w-24 h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${score}%` }} />
        </div>
        <span className={`text-sm font-bold ${color}`}>{score}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat Panel
// ---------------------------------------------------------------------------

function ChatPanel({
  messages,
  moderation,
  sendError,
  sendMessage,
  onMute,
  onBan,
  onShadow,
  onSaveSettings,
}: {
  messages: ChatMessage[];
  moderation: ChatModerationState;
  sendError: string | null;
  sendMessage: (content: string) => Promise<ChatMessage | null>;
  onMute: (participantId: string) => void;
  onBan: (participantId: string) => void;
  onShadow: (participantId: string) => void;
  onSaveSettings: (settings: ChatModerationState["settings"]) => void;
}) {
  const [draft, setDraft] = useState("");
  const [bannedWordsDraft, setBannedWordsDraft] = useState(moderation.settings.bannedWords.join(", "));
  const [rateLimitDraft, setRateLimitDraft] = useState(String(moderation.settings.rateLimitPerMinute));
  const [optimisticMessages, setOptimisticMessages] = useState<LocalPresenterMessage[]>([]);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const cleanupTimerIds = useRef<ReturnType<typeof setTimeout>[]>([]);
  const chatMessages = useMemo(
    () => mergePresenterChatMessages(messages, optimisticMessages),
    [messages, optimisticMessages],
  );

  useEffect(() => () => {
    for (const timerId of cleanupTimerIds.current) {
      clearTimeout(timerId);
    }
    cleanupTimerIds.current = [];
  }, []);

  useEffect(() => {
    const container = listRef.current;
    if (!container || isScrollLocked) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [chatMessages, isScrollLocked]);

  const handleScroll = () => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    setIsScrollLocked(remaining > 32);
  };

  const jumpToLatest = () => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
    setIsScrollLocked(false);
  };

  const submitMessage = async () => {
    const value = draft.trim();
    if (!value) {
      return;
    }

    const optimisticId = createLocalPresenterMessageId();
    setOptimisticMessages((current) => [
      ...current,
      {
        localId: optimisticId,
        senderName: "Presenter",
        senderRole: "presenter",
        text: value,
        timestamp: new Date().toISOString(),
        status: "pending",
      },
    ]);
    setDraft("");
    setIsScrollLocked(false);

    const sent = await sendMessage(value);
    if (sent) {
      setOptimisticMessages((current) => current.map((message) => (
        message.localId === optimisticId
          ? { ...message, serverMessageId: sent.id, senderName: sent.senderName, status: "sent" }
          : message
      )));
      const timerId = setTimeout(() => {
        setOptimisticMessages((current) => current.filter((message) => message.localId !== optimisticId));
      }, 1200);
      cleanupTimerIds.current.push(timerId);
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === optimisticId
        ? { ...message, status: "failed" }
        : message
    )));
  };

  const retryMessage = async (localId: string) => {
    const pending = optimisticMessages.find((message) => message.localId === localId);
    if (!pending) {
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === localId
        ? { ...message, status: "pending", timestamp: new Date().toISOString() }
        : message
    )));

    const sent = await sendMessage(pending.text);
    if (sent) {
      setOptimisticMessages((current) => current.map((message) => (
        message.localId === localId
          ? { ...message, serverMessageId: sent.id, senderName: sent.senderName, status: "sent" }
          : message
      )));
      const timerId = setTimeout(() => {
        setOptimisticMessages((current) => current.filter((message) => message.localId !== localId));
      }, 1200);
      cleanupTimerIds.current.push(timerId);
      return;
    }

    setOptimisticMessages((current) => current.map((message) => (
      message.localId === localId
        ? { ...message, status: "failed" }
        : message
    )));
  };

  return (
    <div className="flex flex-col h-full">
      <PanelHeader title="Chat" count={messages.length} />
      <div className="grid gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 lg:grid-cols-[2fr,1fr]">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Moderation policy</div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span className="rounded-full border border-[var(--color-border)] px-2 py-1">
              {moderation.participants.filter((entry) => entry.isMuted).length} muted
            </span>
            <span className="rounded-full border border-[var(--color-border)] px-2 py-1">
              {moderation.participants.filter((entry) => entry.isBanned).length} banned
            </span>
            <span className="rounded-full border border-[var(--color-border)] px-2 py-1">
              {moderation.participants.filter((entry) => entry.isShadowFiltered).length} shadowed
            </span>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={bannedWordsDraft}
              onChange={(event) => setBannedWordsDraft(event.target.value)}
              placeholder="Custom banned words, comma separated"
              className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
            <input
              type="number"
              min={1}
              max={100}
              value={rateLimitDraft}
              onChange={(event) => setRateLimitDraft(event.target.value)}
              className="w-28 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              aria-label="Rate limit per minute"
            />
            <button
              type="button"
              onClick={() => onSaveSettings({
                ...moderation.settings,
                bannedWords: bannedWordsDraft.split(",").map((entry) => entry.trim()).filter(Boolean),
                rateLimitPerMinute: Number(rateLimitDraft) || 10,
              })}
              className="rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm text-[var(--color-primary-foreground)]"
            >
              Save policy
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-3 text-xs text-[var(--color-muted-foreground)]">
          Shadow-filtered messages stay visible to their sender but are hidden from other attendees. Muted and banned users are rejected at send time on the mounted `/chat` seam.
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
        <span>Presenter chat</span>
        {isScrollLocked ? (
          <button
            type="button"
            onClick={jumpToLatest}
            className="rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary-light)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]"
          >
            Scroll locked • Jump to latest
          </button>
        ) : null}
      </div>
      <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-3 space-y-2">
        {chatMessages.map((msg) => (
          <div key={msg.id} className={`flex gap-2 rounded-lg ${msg.isPinned ? "bg-[var(--color-primary-light)] p-2" : "border border-[var(--color-border)] bg-[var(--color-card)] p-3"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
              msg.senderRole === "presenter" ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                : msg.senderRole === "moderator" ? "bg-[var(--color-warning)]/20 text-[var(--color-warning)]"
                : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
            }`}>
              {msg.senderName[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[var(--color-foreground)]">{msg.senderName}</span>
                {msg.isPinned && <Pin className="h-2.5 w-2.5 text-[var(--color-primary)]" />}
                {msg.shadowFiltered ? (
                  <span className="rounded-full bg-[var(--color-warning)]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-warning)]">
                    Shadow
                  </span>
                ) : null}
                {msg.moderationCategory ? (
                  <span className="rounded-full bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                    {msg.moderationCategory.replace(/_/g, " ")}
                  </span>
                ) : null}
                {"status" in msg ? (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                    msg.status === "failed"
                      ? "bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]"
                      : msg.status === "sent"
                        ? "bg-[var(--color-success)]/15 text-[var(--color-success)]"
                        : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
                  }`}>
                    {msg.status}
                  </span>
                ) : null}
                <span className="text-[10px] text-[var(--color-muted-foreground)]">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
              <p className="text-xs text-[var(--color-foreground)]">{msg.text}</p>
              {"status" in msg && msg.status === "failed" ? (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-[var(--color-destructive)]">Failed to send presenter message.</p>
                  <button
                    type="button"
                    onClick={() => void retryMessage(msg.localId)}
                    className="rounded-full border border-[var(--color-destructive)]/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-destructive)]"
                  >
                    Retry
                  </button>
                </div>
              ) : null}
              {msg.senderRole === "attendee" && msg.senderId ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" onClick={() => onMute(msg.senderId)} className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                    <ShieldAlert className="h-3 w-3" /> Mute
                  </button>
                  <button type="button" onClick={() => onShadow(msg.senderId)} className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                    <EyeOff className="h-3 w-3" /> Shadow
                  </button>
                  <button type="button" onClick={() => onBan(msg.senderId)} className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-destructive)]">
                    <Ban className="h-3 w-3" /> Ban
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      {/* Compose */}
      <div className="border-t border-[var(--color-border)] p-2 flex gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void submitMessage();
            }
          }}
          rows={2}
          placeholder="Message as presenter. Enter sends, Shift+Enter adds a new line."
          className="min-h-[52px] flex-1 resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
        <button
          type="button"
          onClick={() => void submitMessage()}
          className="rounded-md bg-[var(--color-primary)] p-1.5 text-[var(--color-primary-foreground)]"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
      {sendError ? (
        <p className="border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-destructive)]">{sendError}</p>
      ) : null}
    </div>
  );
}

function createLocalPresenterMessageId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mergePresenterChatMessages(messages: ChatMessage[], optimisticMessages: LocalPresenterMessage[]) {
  const optimisticServerIds = new Set(
    optimisticMessages
      .map((message) => message.serverMessageId)
      .filter((value): value is string => typeof value === "string" && value.length > 0),
  );

  const rows = [
    ...messages
      .filter((message) => !optimisticServerIds.has(message.id))
      .map((message) => ({ ...message, id: message.id })),
    ...optimisticMessages.map((message) => ({ ...message, id: message.localId })),
  ];

  return rows.sort((left, right) => (
    new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
  ));
}

// ---------------------------------------------------------------------------
// Q&A Panel
// ---------------------------------------------------------------------------

function QaPanel({ questions }: { questions: QaQuestion[] }) {
  const sorted = [...questions].sort((a, b) => b.upvotes - a.upvotes);
  const unanswered = sorted.filter((q) => !q.isAnswered);
  const answered = sorted.filter((q) => q.isAnswered);

  return (
    <div className="flex flex-col h-full">
      <PanelHeader title="Q&A" count={unanswered.length} subtitle="unanswered" />
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {unanswered.map((q) => (
          <div key={q.id} className="border border-[var(--color-border)] rounded-lg p-3 bg-[var(--color-card)]">
            <p className="text-sm text-[var(--color-foreground)]">{q.question}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-[var(--color-muted-foreground)]">{q.askedBy}</span>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-0.5 text-xs text-[var(--color-muted-foreground)]">
                  <ThumbsUp className="h-3 w-3" /> {q.upvotes}
                </span>
                <button
                  type="button"
                  disabled
                  title="Answer publishing is not wired to the attendee-facing Q&A seam yet."
                  className="flex items-center gap-0.5 text-xs text-[var(--color-muted-foreground)] opacity-60"
                >
                  <Check className="h-3 w-3" /> Answer
                </button>
              </div>
            </div>
          </div>
        ))}
        {answered.length > 0 && (
          <>
            <div className="text-[10px] text-[var(--color-muted-foreground)] uppercase tracking-wide mt-4">Answered ({answered.length})</div>
            {answered.map((q) => (
              <div key={q.id} className="border border-[var(--color-border)] rounded-lg p-3 bg-[var(--color-surface)] opacity-60">
                <p className="text-sm text-[var(--color-foreground)] line-through">{q.question}</p>
                <span className="text-[10px] text-[var(--color-success)]">Answered</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Polls Panel
// ---------------------------------------------------------------------------

function PollsPanel({
  polls,
  resultsById,
  error,
  onCreateAndLaunch,
  onLaunch,
  onClose,
}: {
  polls: WebinarPollRecord[];
  resultsById: Record<string, WebinarPollResults | undefined>;
  error: string | null;
  onCreateAndLaunch: (input: CreateWebinarPollInput) => Promise<string | null>;
  onLaunch: (pollId: string) => Promise<string | null>;
  onClose: (pollId: string) => Promise<string | null>;
}) {
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [pollType, setPollType] = useState<WebinarPollRecord["pollType"]>("single_choice");
  const [options, setOptions] = useState(["", ""]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showResultsToAttendees, setShowResultsToAttendees] = useState(true);

  const requiresOptions = pollType === "single_choice" || pollType === "multiple_choice";

  const resetComposer = () => {
    setQuestion("");
    setPollType("single_choice");
    setOptions(["", ""]);
    setIsAnonymous(false);
    setShowResultsToAttendees(true);
  };

  const submitCreateAndLaunch = async () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      return;
    }

    const preparedOptions = requiresOptions
      ? options
          .map((option, index) => ({ id: `option-${index + 1}`, text: option.trim() }))
          .filter((option) => option.text.length > 0)
      : undefined;

    if (requiresOptions && (!preparedOptions || preparedOptions.length < 2)) {
      return;
    }

    const created = await onCreateAndLaunch({
      question: trimmedQuestion,
      pollType,
      options: preparedOptions,
      isAnonymous,
      showResultsToAttendees,
    });

    if (created) {
      resetComposer();
      setIsComposerOpen(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PanelHeader title="Polls" />
      <div className="flex-1 overflow-y-auto p-4">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[var(--color-foreground)]">Launch Poll</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Create, launch, close, and relaunch polls from the canonical webinar seam.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsComposerOpen((current) => !current)}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm text-[var(--color-primary-foreground)] transition-colors hover:bg-[var(--color-primary-hover)]"
            >
              <Play className="h-3.5 w-3.5" /> {isComposerOpen ? "Hide Form" : "Launch Poll"}
            </button>
          </div>

          {isComposerOpen ? (
            <div className="mt-4 space-y-4 border-t border-[var(--color-border)] pt-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Question</label>
                <textarea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  rows={3}
                  placeholder="What should we ask the audience?"
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Poll type</label>
                  <select
                    value={pollType}
                    onChange={(event) => setPollType(event.target.value as WebinarPollRecord["pollType"])}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  >
                    <option value="single_choice">Single choice</option>
                    <option value="multiple_choice">Multiple choice</option>
                    <option value="word_cloud">Word cloud</option>
                    <option value="rating">Rating</option>
                    <option value="open_text">Open text</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2 pt-6">
                  <label className="inline-flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                    <input type="checkbox" checked={isAnonymous} onChange={(event) => setIsAnonymous(event.target.checked)} />
                    Anonymous responses
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                    <input type="checkbox" checked={showResultsToAttendees} onChange={(event) => setShowResultsToAttendees(event.target.checked)} />
                    Show results to attendees
                  </label>
                </div>
              </div>

              {requiresOptions ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Options</label>
                    <button
                      type="button"
                      onClick={() => setOptions((current) => [...current, ""])}
                      className="text-xs font-medium text-[var(--color-primary)]"
                    >
                      Add option
                    </button>
                  </div>
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(event) => setOptions((current) => current.map((entry, optionIndex) => optionIndex === index ? event.target.value : entry))}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                      />
                      <button
                        type="button"
                        onClick={() => setOptions((current) => current.length > 2 ? current.filter((_, optionIndex) => optionIndex !== index) : current)}
                        className="rounded-md border border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-muted-foreground)]"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetComposer();
                    setIsComposerOpen(false);
                  }}
                  className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void submitCreateAndLaunch()}
                  className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)]"
                >
                  Create & Launch
                </button>
              </div>
            </div>
          ) : null}

          {error ? (
            <p className="mt-3 text-xs text-[var(--color-destructive)]">{error}</p>
          ) : null}
        </div>

        <div className="mt-4 space-y-3">
          {polls.length === 0 ? (
            <div className="flex items-center justify-center rounded-2xl border border-dashed border-[var(--color-border)] p-8 text-center">
              <div>
                <BarChart3 className="mx-auto mb-3 h-10 w-10 text-[var(--color-muted-foreground)]" />
                <p className="text-sm font-medium text-[var(--color-foreground)]">No polls yet</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Create and launch the first audience poll from this panel.</p>
              </div>
            </div>
          ) : polls.map((poll) => (
            <div key={poll.id} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                      {poll.pollType.replace(/_/g, " ")}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                      poll.status === "active"
                        ? "bg-[var(--color-success)]/15 text-[var(--color-success)]"
                        : poll.status === "closed"
                          ? "bg-[var(--color-warning)]/15 text-[var(--color-warning)]"
                          : "bg-[var(--color-primary-light)] text-[var(--color-primary)]"
                    }`}>
                      {poll.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-[var(--color-foreground)]">{poll.question}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {poll.isAnonymous ? "Anonymous" : "Named"} • {poll.showResultsToAttendees ? "Results visible to attendees" : "Results hidden from attendees"}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {poll.status === "active" ? (
                    <button
                      type="button"
                      onClick={() => void onClose(poll.id)}
                      className="rounded-md border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)]"
                    >
                      Close poll
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void onLaunch(poll.id)}
                      className="rounded-md bg-[var(--color-primary)] px-3 py-2 text-xs font-medium text-[var(--color-primary-foreground)]"
                    >
                      {poll.status === "closed" ? "Relaunch" : "Launch"}
                    </button>
                  )}
                </div>
              </div>

              {Array.isArray(poll.options) && poll.options.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {poll.options.map((option) => (
                    <span key={option.id} className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
                      {option.text}
                    </span>
                  ))}
                </div>
              ) : null}

              {resultsById[poll.id] ? (
                <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                    Live results • {resultsById[poll.id]?.totalResponses ?? 0} responses
                  </p>
                  <div className="mt-3 space-y-2">
                    {resultsById[poll.id]?.counts?.map((count) => (
                      <div key={count.optionId}>
                        <div className="flex items-center justify-between gap-2 text-xs text-[var(--color-foreground)]">
                          <span>{count.label}</span>
                          <span>{count.responses}</span>
                        </div>
                      </div>
                    ))}
                    {typeof resultsById[poll.id]?.average === "number" ? (
                      <p className="text-sm text-[var(--color-foreground)]">Average rating: {resultsById[poll.id]?.average}</p>
                    ) : null}
                    {resultsById[poll.id]?.textResponses?.map((response, index) => (
                      <p key={index} className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-foreground)]">
                        {response}
                      </p>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attendees Panel
// ---------------------------------------------------------------------------

function AttendeesPanel({
  attendees,
  queue,
  spotlight,
  onLowerHand,
  onLowerAllHands,
  onSpotlight,
  onRemoveSpotlight,
}: {
  attendees: AttendeeInfo[];
  queue: RaisedHandEntry[];
  spotlight: SpotlightState;
  onLowerHand: (participantId: string) => Promise<boolean>;
  onLowerAllHands: () => Promise<boolean>;
  onSpotlight: (participantId: string) => Promise<boolean>;
  onRemoveSpotlight: (participantId: string) => Promise<boolean>;
}) {
  const active = attendees.filter((a) => a.isActive);
  const inactive = attendees.filter((a) => !a.isActive);

  return (
    <div data-testid="attendees-panel" className="flex flex-col h-full">
      <PanelHeader title="Attendees" count={active.length} subtitle={`of ${attendees.length} total`} />
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <div data-testid="hand-raise-queue" className="mb-4 rounded-xl border border-gray-800 bg-gray-950/60 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Raised hands</p>
              <p className="mt-1 text-sm text-white">{queue.length} waiting for host attention</p>
            </div>
            <button
              type="button"
              onClick={() => void onLowerAllHands()}
              disabled={queue.length === 0}
              className="rounded-full border border-gray-700 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-200 disabled:opacity-50"
            >
              Lower all
            </button>
          </div>
          {spotlight.participantId ? (
            <div
              data-testid="spotlight-status"
              className="mt-3 flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 px-3 py-2"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-primary">Spotlight active</p>
                <p data-testid="spotlight-participant" className="mt-1 text-sm text-white">
                  {spotlight.displayName ?? `Attendee ${spotlight.participantId.slice(0, 8)}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void onRemoveSpotlight(spotlight.participantId!)}
                className="rounded-full border border-primary/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary"
              >
                Remove spotlight
              </button>
            </div>
          ) : null}
          <div className="mt-3 space-y-2">
            {queue.length === 0 ? (
              <p className="text-xs text-[var(--color-muted-foreground)]">No hands raised right now.</p>
            ) : queue.map((entry) => (
              <div
                key={entry.participantId}
                data-testid={`hand-raise-item-${entry.participantId}`}
                className="flex items-center gap-2 rounded-xl border border-gray-800 px-3 py-2"
              >
                <div className="flex-1">
                  <p className="text-sm text-white">{entry.displayName ?? `Attendee ${entry.participantId.slice(0, 8)}`}</p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                    Raised at {formatRelativeTimestamp(entry.raisedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void onSpotlight(entry.participantId)}
                  className="rounded-full border border-primary/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary"
                >
                  Spotlight
                </button>
                <button
                  type="button"
                  onClick={() => void onLowerHand(entry.participantId)}
                  className="rounded-full border border-gray-700 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-200"
                >
                  Lower hand
                </button>
              </div>
            ))}
          </div>
        </div>
        {active.map((a) => (
          <AttendeeRow key={a.id} attendee={a} />
        ))}
        {inactive.length > 0 && (
          <>
            <div className="text-[10px] text-[var(--color-muted-foreground)] uppercase tracking-wide mt-3">Inactive ({inactive.length})</div>
            {inactive.map((a) => (
              <AttendeeRow key={a.id} attendee={a} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function formatRelativeTimestamp(value: string | null | undefined) {
  if (!value) {
    return "just now";
  }

  const deltaMs = Date.now() - new Date(value).getTime();
  const deltaSeconds = Math.max(0, Math.round(deltaMs / 1000));
  if (deltaSeconds < 60) {
    return `${deltaSeconds}s ago`;
  }

  const deltaMinutes = Math.round(deltaSeconds / 60);
  return `${deltaMinutes}m ago`;
}

function AttendeeRow({ attendee }: { attendee: AttendeeInfo }) {
  const scoreColor = attendee.engagementScore >= 70 ? "text-[var(--color-success)]"
    : attendee.engagementScore >= 40 ? "text-[var(--color-warning)]"
    : "text-[var(--color-muted-foreground)]";

  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${!attendee.isActive ? "opacity-50" : ""}`}>
      <div className="relative">
        <div className="w-6 h-6 rounded-full bg-[var(--color-surface)] flex items-center justify-center text-[10px] font-bold text-[var(--color-muted-foreground)]">
          {attendee.name[0]?.toUpperCase()}
        </div>
        {attendee.isActive && (
          <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--color-success)] border border-[var(--color-card)]" />
        )}
      </div>
      <span className="text-xs text-[var(--color-foreground)] flex-1 truncate">{attendee.name}</span>
      <span className={`text-[10px] font-mono ${scoreColor}`}>{attendee.engagementScore}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTA Panel
// ---------------------------------------------------------------------------

function CtaPanel({ webinarId }: { webinarId: string | undefined }) {
  return (
    <div className="flex flex-col h-full">
      <PanelHeader title="CTAs & Offers" />
      <div className="flex-1 overflow-y-auto p-4">
        <CtaSequenceManager webinarId={webinarId} compact />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Co-Pilot Panel
// ---------------------------------------------------------------------------

function CopilotPanel({ suggestions }: { suggestions: CopilotSuggestion[] }) {
  const [sensitivity, setSensitivity] = useState<"low" | "medium" | "high">("medium");
  const [quietMode, setQuietMode] = useState(false);

  const activeSuggestions = suggestions.filter((s) => !s.dismissedAt);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between border-b border-[var(--color-ai-border)] px-4 py-2.5 bg-[var(--color-ai-light)]">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-[var(--color-ai)]" />
          <span className="text-sm font-semibold text-[var(--color-ai)]">AI Co-Pilot</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-ai)]/10 text-[var(--color-ai)] border border-[var(--color-ai-border)]">
            {activeSuggestions.length} suggestions
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setQuietMode(!quietMode)}
            className={`text-xs px-2 py-1 rounded-md border transition-colors ${
              quietMode
                ? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] border-[var(--color-border)]"
                : "bg-[var(--color-ai-light)] text-[var(--color-ai)] border-[var(--color-ai-border)]"
            }`}
          >
            {quietMode ? <VolumeX className="h-3 w-3 inline mr-1" /> : <Volume2 className="h-3 w-3 inline mr-1" />}
            {quietMode ? "Quiet" : "Active"}
          </button>
          <select
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value as "low" | "medium" | "high")}
            className="text-xs rounded-md border border-[var(--color-ai-border)] bg-[var(--color-card)] px-2 py-1 text-[var(--color-foreground)]"
            aria-label="Co-Pilot sensitivity"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {quietMode ? (
          <div className="text-center py-8">
            <VolumeX className="h-8 w-8 mx-auto text-[var(--color-muted-foreground)] mb-2" />
            <p className="text-sm text-[var(--color-muted-foreground)]">Quiet mode active</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">Suggestions paused for 5 minutes</p>
          </div>
        ) : activeSuggestions.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="h-8 w-8 mx-auto text-[var(--color-ai)]/30 mb-2" />
            <p className="text-sm text-[var(--color-muted-foreground)]">No suggestions right now</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">Co-Pilot is monitoring your webinar</p>
          </div>
        ) : (
          activeSuggestions.map((s) => (
            <SuggestionCard key={s.id} suggestion={s} />
          ))
        )}
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: CopilotSuggestion }) {
  const priorityColor = suggestion.priority === "high" ? "border-l-[var(--color-destructive)]"
    : suggestion.priority === "medium" ? "border-l-[var(--color-warning)]"
    : "border-l-[var(--color-border)]";

  return (
    <div className={`border border-[var(--color-ai-border)] border-l-4 ${priorityColor} rounded-lg p-3 bg-[var(--color-card)]`}>
      <p className="text-xs text-[var(--color-foreground)]">{suggestion.message}</p>
      <div className="flex items-center gap-2 mt-2">
        {suggestion.actionLabel && (
          <button
            type="button"
            disabled
            title="Co-Pilot action handlers are advisory only until the automation seam is wired."
            className="text-xs font-medium text-[var(--color-muted-foreground)] opacity-60"
          >
            {suggestion.actionLabel}
          </button>
        )}
        <button
          type="button"
          disabled
          title="Dismissal persistence is not wired to the Co-Pilot suggestion seam yet."
          className="ml-auto text-xs text-[var(--color-muted-foreground)] opacity-60"
        >
          Dismiss unavailable
        </button>
      </div>
      <p className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">
        Co-Pilot suggestions are read-only in this build until action and dismissal handlers are mounted.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function PanelHeader({ title, count, subtitle }: { title: string; count?: number; subtitle?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2.5 bg-[var(--color-card)]">
      <h2 className="text-sm font-semibold text-[var(--color-foreground)]">{title}</h2>
      {count != null && (
        <span className="text-xs text-[var(--color-muted-foreground)]">
          {count} {subtitle ?? ""}
        </span>
      )}
    </div>
  );
}

function StatusPill({
  label,
  tone,
  testId,
}: {
  label: string;
  tone: "default" | "success" | "warning" | "danger" | "disabled";
  testId?: string;
}) {
  const toneClassName = tone === "success"
    ? "border-[var(--color-success)]/20 bg-[var(--color-success)]/10 text-[var(--color-success)]"
    : tone === "warning"
      ? "border-[var(--color-warning)]/20 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
      : tone === "danger"
        ? "border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
      : tone === "disabled"
        ? "border-[var(--color-border)] bg-[var(--color-muted)]/40 text-[var(--color-muted-foreground)]"
        : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)]";

  return (
    <span data-testid={testId} className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${toneClassName}`}>
      {label}
    </span>
  );
}

function SurfaceStateBadge({ state }: { state: ControlSurfaceState }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
      state === "live"
        ? "bg-[var(--color-success)]/15 text-[var(--color-success)]"
        : "bg-[var(--color-muted)]/40 text-[var(--color-muted-foreground)]"
    }`}>
      {state === "live" ? "Live" : "Disabled"}
    </span>
  );
}
